import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface NumberFieldProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min: number;
  max: number;
  /** It must be filled in: its label is marked with a star. */
  required?: boolean;
  error?: string | undefined;
}

const whole = (text: string): number | null =>
  /^\d+$/.test(text.trim()) ? Number(text) : null;

/**
 * A whole number such as days, in place of `<input type="number">` (docs/02 §6): typed, or
 * stepped with − and + buttons that stay inside `min` and `max`.
 */
export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  error,
  required = false,
}: NumberFieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState(value === null ? '' : String(value));
  const typed = whole(text);
  const outside = typed !== null && (typed < min || typed > max);
  // Empty is "not filled in", which the form says; only text that is not a number is wrong here.
  const message =
    typed === null && text.trim() !== ''
      ? t('field.number')
      : outside
        ? t('problem.range')
        : error;

  const set = (next: number) => {
    setText(String(next));
    onChange(next);
  };

  return (
    <div className="field">
      <label className="field-label" htmlFor={id} data-required={required || undefined}>
        {label}
      </label>
      <div className="number">
        <button
          type="button"
          className="icon-button"
          aria-label={t('field.less', { label })}
          disabled={typed === null || typed <= min}
          onClick={() => {
            // Disabled while nothing is typed, so `typed` is a number here.
            set(Math.max(min, Number(typed) - 1));
          }}
        >
          −
        </button>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={text}
          aria-invalid={message !== undefined}
          aria-describedby={message === undefined ? undefined : `${id}-error`}
          onChange={(event) => {
            setText(event.target.value);
            const next = whole(event.target.value);
            onChange(next !== null && next >= min && next <= max ? next : null);
          }}
        />
        <button
          type="button"
          className="icon-button"
          aria-label={t('field.more', { label })}
          disabled={typed !== null && typed >= max}
          onClick={() => {
            set(Math.min(max, (typed ?? min - 1) + 1));
          }}
        >
          +
        </button>
      </div>
      {message !== undefined && (
        <p className="field-error" id={`${id}-error`}>
          {message}
        </p>
      )}
    </div>
  );
}
